import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import "./InfoTooltip.css";

const GAP = 8;
const BUBBLE_HALF_WIDTH = 120;
const EDGE_MARGIN = 12;
const ESTIMATED_BUBBLE_HEIGHT = 90;

// Shared "what does this mean" icon used next to metric labels, KPI cards, and
// chart titles across Dashboard/Homepage/Inbound/Outbound - hover on desktop,
// tap on touch devices (toggled via onClick so it also works without hover support).
//
// The bubble is rendered via a portal straight onto document.body, positioned with
// fixed coordinates computed from the trigger's own bounding box. Rendering it inline
// used to get clipped by any ancestor with overflow:hidden (the outbound flip-card,
// dashboard panels, etc.) since it was just a normal DOM descendant of them - a portal
// escapes that entirely. It also stops the bubble from inheriting an ancestor label's
// text-transform/letter-spacing (most metric labels render in uppercase small-caps).
export const InfoTooltip = ({ text, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const placement = rect.top < ESTIMATED_BUBBLE_HEIGHT + GAP ? "bottom" : "top";
    const rawLeft = rect.left + rect.width / 2;
    const clampedLeft = Math.min(
      Math.max(rawLeft, EDGE_MARGIN + BUBBLE_HALF_WIDTH),
      window.innerWidth - EDGE_MARGIN - BUBBLE_HALF_WIDTH,
    );

    setCoords({
      top: placement === "top" ? rect.top - GAP : rect.bottom + GAP,
      left: clampedLeft,
      placement,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  if (!text) return null;

  return (
    <span
      className={`info-tooltip ${className}`.trim()}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        ref={triggerRef}
        className="info-tooltip__trigger"
        aria-label="More information"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <Info size={13} strokeWidth={2.2} />
      </button>
      {open && coords
        ? createPortal(
            <span
              className={`info-tooltip__bubble info-tooltip__bubble--${coords.placement}`}
              role="tooltip"
              style={{ top: coords.top, left: coords.left }}
            >
              {text}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
};

export default InfoTooltip;
